import type { LoginResponse } from '@/api/useLogin';

/**
 * What the server got when it logged an OTP landing in while composing the
 * document. Serialisable; streams to the client behind the HTML.
 *
 * The server does two things the client would otherwise do seconds later: the
 * login request and `portal.login.completed`. Everything after the response —
 * session commit, RUM, redirect — stays on the client, which takes this in
 * place of a request of its own.
 */
export interface IServerOtpLogin {
  /**
   * The app session the server opened for this landing, stamped on the login
   * and the event; the client adopts it so the events it sends afterwards
   * belong to the same session.
   */
  sessionId: string;
  /** The gateway's 2xx body, business failures (`errorCode`) included. */
  response?: LoginResponse;
  /** A non-2xx answer, for the client to fail the login the way it would its own. */
  rejection?: { status: number; data: unknown };
  /** Whether `portal.login.completed` already reached the publisher from the server. */
  eventEmitted: boolean;
}

/**
 * The failure the client's login would have thrown for the same answer, so
 * `classifyLoginFailureReason` and `handleLoginError` need no second path.
 */
export const toLoginRequestError = (rejection: NonNullable<IServerOtpLogin['rejection']>) => ({
  response: { status: rejection.status, data: rejection.data },
  message: `Request failed with status code ${rejection.status}`,
});
